import Vue from 'vue'
import axios from '@/libs/axios'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import {
  hasValue,
  designateValue,
  designateOptions,
  transformField,
  checkHiddenAttr,
  checkIfStepCanBeHidden,
} from '@/helpers/formConditions'

const defaultState = () => {
  return {
    type: 'Camper',
    userId: null,
    programId: null,
    applicationId: null,
    editingStateVersion: null,
    program: {},
    userAttributes: [],
    userData: {},
    userFieldResults: [],
    hidePrice: false,
    paidAmountForPc: 0,
    limitSessionSelection: false,
    sessionsList: [],
    productsList: [],
    stepsList: [],
    stepsValues: [],
    chosenSessions: [],
    productPurchases: [],
    selectedSessionsList: [],
    selectedGroupsList: [],
    applicationStatus: null,
    isApplicationAccepted: false,
    applicationPaymentOption: null,
    paymentContract: {},
    paymentContractIsExist: false,
    paymentFees: [],
    allSelectedGroups: [],
    groupsForAutoAssign: [],
    groupsForPC: [],
    currentTabIndex: 0,
    selectedGroupForViewingUsers: null,
    allConditions: [],
    allFormFields: [],
    isDataFullPrepared: true,
    editingApplicationPermissions: [],
    crossProgramsConditions: [],
    crossProgramConditionalFieldsData: [],
    familySettings: [],
    familyFormFields: [],
    familyFormConditions: [],
    familyData: {},
    familyFormStatusInfo: {
      isFilledFamilyForm: false,
      isConfirmedFamilyForm: false,
      familyFormStatus: null,
      isDisableToFillByFamilyForm: false,
    },
    isLoading: false,
    groupSelectionFields: [],
    groupSelectionDependsMap: [],
    groupSelectionDependsFields: [],
    groupSelectionFormState: [],
    isHideStepsBeforeAcceptance: false,
  }
}

function isStepEditable(step, state, index) {
  const { editingApplicationPermissions, stepsList, isApplicationAccepted } = state

  const lastApprovedByAdminStepIndex = stepsList.findLastIndex(s =>
    ['Requires Approval', 'Requires Approval To Continue'].includes(s.requirement_option) && s.status === 'Approved' && !s.hidden
  )

  // 1. Enable to edit only specific steps
  if (
    editingApplicationPermissions.enable_edit_specific_steps &&
    Array.isArray(editingApplicationPermissions.specific_steps) &&
    editingApplicationPermissions.specific_steps.includes(step.id)
  ) {
    return true
  }

  // 2. Disable edit by PC
  if (step.isDisabledEditingByPc && step.status !== 'Rejected') {
    return false
  }

  // 3. Exist approved steps by admin, disable to edit previous steps
  if (lastApprovedByAdminStepIndex !== -1 && index <= lastApprovedByAdminStepIndex && step.status !== 'Rejected') {
    return false
  }

  // 4. Already accepted application, Approved and In Review — disable to edit
  if (isApplicationAccepted && ['Approved', 'In Review'].includes(step.status)) {
    return false
  }

  // 5. Enable edit all steps
  if (editingApplicationPermissions.enable_edit_all_steps) {
    return true
  }

  // 6. Disable to edit after specific date if exists
  if (editingApplicationPermissions.disable_editing_whole_application) {
    if (editingApplicationPermissions.specific_date) {
      return moment().isBefore(editingApplicationPermissions.specific_date)
    }
    return false
  }

  // 7. Disable to edit specific statuses and after specific date if exists
  if (
    editingApplicationPermissions.disable_editing_steps &&
    Array.isArray(editingApplicationPermissions.specific_step_statuses)
  ) {
    if (!step.status) return true

    let editable = !editingApplicationPermissions.specific_step_statuses.includes(step.status)

    if (editingApplicationPermissions.specific_date) {
      editable = moment().isBefore(editingApplicationPermissions.specific_date)
    }

    return editable
  }

  // 8. Enable to edit by default
  return true
}


export default {
  namespaced: true,
  state: defaultState(),
  getters: {
    getIsLoading: state => state.isLoading,
    getProgramName: state => state.program.name,
    getProgramID: state => state.program.id,
    getProgramCompleteRegistrationBtnText: state => state.program.complete_registration_btn_text,
    getApplicationId: state => state.applicationId,
    getEditingStateVersion: state => state.editingStateVersion,
    getUserId: state => state.userId,
    getUserName: state => state.userData.full_name ?? '',
    getUserAttributes: state => state.userAttributes,
    getUserFieldResults: state => state.userFieldResults,
    getHidePrice: state => state.hidePrice,
    getLimitSessionSelection: state => state.limitSessionSelection,
    getSessionsList: state => state.sessionsList,
    getStepsList: state => state.stepsList,
    getStepsValues: state => state.stepsValues,
    getPaidAmountForPc: state => state.paidAmountForPc,
    getChosenSessions: state => state.chosenSessions,
    getSelectedSessionsList: state => state.selectedSessionsList,
    getProductsList: state => state.productsList,
    getProductPurchases: state => state.productPurchases,
    getSelectedGroupsListOnlyFormApplication: state => state.selectedGroupsList,
    getAllSelectedGroups: state => state.allSelectedGroups,
    getSelectedGroupsForPC: state => state.allSelectedGroups.filter(group => group.has_tuition_fee && group.price != 0),
    getGroupsForAutoAssign: state => state.groupsForAutoAssign,
    getApplicationStatus: state => state.applicationStatus,
    getIsApplicationAccepted: state => state.isApplicationAccepted,
    getApplicationPaymentOption: state => state.applicationPaymentOption,
    getPaymentContract: state => state.paymentContract,
    getPaymentContractIsExist: state => state.paymentContractIsExist,
    getPaymentFees: state => state.paymentFees,
    getCurrentTabIndex: state => state.currentTabIndex,
    getSelectedGroupForViewingUsers: state => state.selectedGroupForViewingUsers,
    getAllConditions: state => state.allConditions,
    getAllFormFields: state => state.allFormFields,
    getFilteredStepsList: state => state.stepsList.filter(step => !step.hidden && !step.hiddenByAcceptanceRule),
    getStepById: (state) => (currentStepId) => {
      return state.stepsList.filter(step => !step.hidden).find(step => step.id === currentStepId)
    },
    getHiddenStepsIds: state => state.stepsList.filter(step => step.hidden).map(step => ({
      stepId: step.id,
      hiddenFromStepId: step.hiddenByStep
    })),
    getLastStep: state => state.stepsList.filter(step => !step.hidden && !step.hiddenByAcceptanceRule).slice(-1)[0],
    getIsDataFullPrepared: state => state.isDataFullPrepared,
    getEditingApplicationPermissions: state => state.editingApplicationPermissions,
    getCrossProgramsConditions: state => state.crossProgramsConditions,
    getIsExistCrossProgramsConditions: state => state.crossProgramsConditions.length > 0,
    getCrossProgramConditionalFieldsData: state => state.crossProgramConditionalFieldsData,
    getFamilySettings: state => state.familySettings,
    getIsUseFamilyForm: state => {
      if (!state.familySettings?.use_family_form) {
        return false
      }
      if (state.type === 'Camper' && state.familySettings.form_types.includes('camper')) {
        return true
      }
      if (state.type === 'Staff' && state.familySettings.form_types.includes('staff')) {
        return true
      }
      return false
    },
    getFamilyFormFields: state => state.familyFormFields,
    getFamilyFormConditions: state => state.familyFormConditions,
    getFamilyData: state => state.familyData,
    getIsFilledFamilyForm: state => state.familyFormStatusInfo.isFilledFamilyForm,
    getFamilyFormStatus: state => state.familyFormStatusInfo.familyFormStatus,
    getFamilyFormStatusInfo: state => state.familyFormStatusInfo,
    getGroupSelectionFields: state => state.groupSelectionFields,
    getGroupSelectionDependsMap: state => state.groupSelectionDependsMap,
    getGroupSelectionDependsFields: state => state.groupSelectionDependsFields,
    getGroupSelectionFormState: state => state.groupSelectionFormState,
  },
  mutations: {
    SET_IS_LOADING: (state, data) => {
      state.isLoading = data
    },
    SET_USER_ID: (state, data) => {
      state.userId = data
    },
    SET_APPLICATION_ID: (state, data) => {
      state.applicationId = data
    },
    SET_EDITING_STATE_VERSION: (state, data) => {
      state.editingStateVersion = data
    },
    SET_PROGRAM_ID: (state, data) => {
      state.program.id = data
      state.programId = data
    },
    SET_PAYMENT_FEES: (state, data) => {
      state.paymentFees = data
    },
    SET_TYPE: (state, data) => {
      state.type = data
    },
    SET_USER_ATTRIBUTES: (state, data) => {
      state.userAttributes = data
    },
    SET_USER_DATA: (state, data) => {
      state.userData = data
    },
    SET_USER_FIELD_RESULTS: (state, data) => {
      state.userFieldResults = data
    },
    SET_FAMILY_DATA: (state, data) => {
      state.familyData = data
    },
    SET_IS_FILLED_FAMILY_FORM: (state, data) => {
      state.familyFormStatusInfo.isFilledFamilyForm = data

      if (data) {
        const flags = (state.familySettings.requirement_options || '').split('|')
        const needsApproval = flags.includes('approval')

        if (needsApproval) {
          state.familyFormStatusInfo.familyFormStatus = "In Review"
          state.familyFormStatusInfo.isDisableToFillByFamilyForm = true
        }
      }
    },
    SET_FAMILY_SETTINGS: (state, data) => {
      state.familySettings = data
    },
    SET_FAMILY_FORM_STATUS_INFO: (state) => {
      const flags = (state.familySettings.requirement_options || '').split('|')
      const submissions = state.familyData.family.family_form_submissions || []
      const currentFamilySettingId = state.familySettings.id
      const currentProgramId = state.familySettings.program_id
      const currentUserId = state.userId

      // Submission for current family_setting и and current user
      const currentSubmission = submissions.find(
        s => s.family_setting_id == currentFamilySettingId && s.user_id == currentUserId
      )

      // Approved Submission in the current program for any user
      const approvedInCurrentProgram = submissions.some(
        s => s.family_setting_id == currentFamilySettingId &&
          s.program_id === currentProgramId &&
          s.status === 'Approved'
      )

      // Approved Submission in any program for any user
      const approvedAnywhere = submissions.some(
        s => s.status === 'Approved'
      )

      const isRequired = flags.includes('required')
      const needsApproval = flags.includes('approval')
      const checkFilledCurrent = flags.includes('filled_current')
      const checkFilledElsewhere = flags.includes('filled_elsewhere')

      const isFilledInCurrentProgramByUser = !!currentSubmission
      const isConfirmedInCurrentProgramByUser = currentSubmission?.status === 'Approved'


      let isFilledFamilyForm
      if (isFilledInCurrentProgramByUser && isConfirmedInCurrentProgramByUser) {
        isFilledFamilyForm = true
      } else if (!isFilledInCurrentProgramByUser && checkFilledElsewhere && approvedAnywhere) {
        isFilledFamilyForm = true
      } else if (!isFilledInCurrentProgramByUser && checkFilledCurrent && approvedInCurrentProgram) {
        isFilledFamilyForm = true
      } else {
        isFilledFamilyForm = false
      }

      let status
      if (currentSubmission) {
        status = currentSubmission.status
      } else if (checkFilledCurrent && approvedInCurrentProgram) {
        status = 'Approved'
      } else if (checkFilledElsewhere && approvedAnywhere) {
        status = 'Approved'
      } else {
        status = ''
      }

      let isConfirmedFamilyForm
      if (isConfirmedInCurrentProgramByUser) {
        isConfirmedFamilyForm = true
      } else if (checkFilledCurrent && approvedInCurrentProgram) {
        isConfirmedFamilyForm = true
      } else if (checkFilledElsewhere && approvedAnywhere) {
        isConfirmedFamilyForm = true
      } else {
        isConfirmedFamilyForm = false
      }


      let isDisabled
      if (!isRequired && !needsApproval && !checkFilledCurrent && !checkFilledElsewhere) {
        // 'not_required' — no family form requirement whatsoever
        isDisabled = false
      } else {
        // 'required', 'required|approval', 'not_required|filled_current', 'not_required|filled_elsewhere'
        // All require confirmation: either the current user is approved, or the relevant
        // cross-program/cross-user approval condition (checkFilledCurrent / checkFilledElsewhere) is satisfied.
        isDisabled = !isConfirmedFamilyForm
      }

      state.familyFormStatusInfo.isFilledFamilyForm = isFilledFamilyForm
      state.familyFormStatusInfo.isConfirmedFamilyForm = isConfirmedFamilyForm
      state.familyFormStatusInfo.familyFormStatus = status
      state.familyFormStatusInfo.isDisableToFillByFamilyForm = isDisabled
      state.familyFormStatusInfo.isFilledByUser = isFilledInCurrentProgramByUser
    },
    SET_FAMILY_FORM_VALUES: state => {
      state.familyFormFields.forEach(field => {
        let value
        if (field.section_type === 'parent'
          && state.familyData.primary_parent
        ) {
          if (field.use_prefill) {
            const foundedField = state.familyData.primary_parent.field_results.find(fieldResult => fieldResult.field_id === field.field_id)
            if (foundedField) {
              value = foundedField.result
            } else if (state.familyData.primary_parent.attributes) {
              const fieldName = field.attribute_field_name ? field.attribute_field_name : field.name
              value = state.familyData.primary_parent.attributes[fieldName]
            }
          }
        }

        if (field.section_type === 'secondary_parent'
          && state.familyData.secondary_parent
        ) {
          if (field.use_prefill) {
            const foundedField = state.familyData.secondary_parent.field_results.find(fieldResult => fieldResult.field_id === field.field_id)
            if (foundedField) {
              value = foundedField.result
            } else if (state.familyData.secondary_parent.attributes) {
              const fieldName = field.attribute_field_name ? field.attribute_field_name : field.name
              value = state.familyData.secondary_parent.attributes[fieldName]
            }
          }
        }

        if (field.section_type === 'family') {
          if (field.use_prefill) {
            const foundedField = state.familyData.family.field_results.find(fieldResult => fieldResult.field_id === field.field_id)
            if (foundedField) {
              value = foundedField.result
            } else if (state.familyData.family.attributes) {
              if (field.attribute_field_name in state.familyData.family.attributes) {
                value = state.familyData.family.attributes[field.attribute_field_name]
              } else {
                value = state.familyData.family.attributes[field.name]
              }
            }
          }
        }

        field.value = designateValue(field.type, value, field.options)
      })
    },
    SET_FAMILY_FORM: (state, data) => {
      state.familyFormConditions = data.conditions
      state.familyFormFields = data.fields.map((field, index) => {
        const options = designateOptions(field.type, field.options)
        const ref = field.name + index

        return {
          id: uuidv4(),
          field_id: field.id,
          step_id: null,
          ref: ref,
          value: designateValue(field.type, field.default, options),
          label: field.label,
          name: field.name,
          options: options,
          settings: field.settings,
          type: transformField(field.type),
          required: field.required,
          hidden: checkHiddenAttr(field.conditions.filter(item => item.action === 'Show')),
          use_prefill: field.use_prefill,
          attribute_field_name: field.attribute_field_name,
          section_type: field.section_type,
          readonly: field.readonly,
        }
      })
    },
    ADD_SECONDARY_PARENT: state => {
      const newFields = state.familyFormFields
        .filter(field => field.section_type === 'parent')
        .map(field => {
          return {
            ...field,
            id: uuidv4(),
            value: designateValue(field.type, field.default, field.options),
            section_type: 'secondary_parent',
          }
        })

      state.familyFormFields = [...newFields, ...state.familyFormFields]
    },
    REMOVE_SECONDARY_PARENT: state => {
      state.familyFormFields = state.familyFormFields
        .filter(field => field.section_type !== 'secondary_parent')
    },
    SET_PROGRAM: (state, data) => {
      state.program = data
      state.hidePrice = data.is_hide_price
      state.limitSessionSelection = data.is_only_one_session_selection_allowed
      state.sessionsList = data.sessions.map(item => {
        return ({
          ...item,
          price: Number(item.price),
          front_check: item.allowed_individual_selection
        })
      })
      state.productsList = [...data.products]
      state.stepsList = data.steps[state.type].map(step => ({
        ...step,
        status: '',
        isDisabled: true,
        isDisabledEditingByPc: false,
        hasPC: step.has_payment_contract && data.payment_contract
      }))
      state.editingApplicationPermissions = data.editing_permissions[state.type]
      state.crossProgramsConditions = data.cross_programs_conditions[state.type]
      state.isHideStepsBeforeAcceptance = data.is_hide_steps_before_acceptance[state.type]


      //Prepare group selection depend selection map
      state.groupSelectionFields = state.stepsList
        .flatMap(step => step.form.fields)
        .filter(field => field.type === 'group_selection')

      state.groupSelectionDependsMap = state.groupSelectionFields.reduce((acc, field) => {
        const dependsOn = field.settings?.depends_on || []
        dependsOn.forEach(dependsField => {
          if (!acc[dependsField]) {
            acc[dependsField] = []
          }
          acc[dependsField].push(field.id) // field.name
        })
        return acc
      }, {})

      state.groupSelectionDependsFields = Array.from(
        new Set(
          state.groupSelectionFields.flatMap(field => field.settings?.depends_on || [])
        )
      )
    },
    SET_EDITABLE_SETTINGS_FOR_STEPS: state => {
      state.stepsList.forEach((step, index) => {
        step.isEditable = isStepEditable(step, state, index)
      })
    },
    SET_HIDDEN_STATUS_FOR_STEPS_FROM_SETTINGS: (state, { potentialStatus = null } = {}) => {
      if (!state.isHideStepsBeforeAcceptance) {
        return
      }

      if (!Array.isArray(state.stepsList)) {
        return
      }

      state.stepsList.forEach((step, index) => {
        if (!step.is_in_acceptable_form) {
          return
        }

        if (potentialStatus && potentialStatus.is_acceptable) {
          step.hiddenByAcceptanceRule = false
        } else {
          step.hiddenByAcceptanceRule = !state.isApplicationAccepted
        }
        Vue.set(state.stepsList, index, step)
      })
    },
    UPDATE_STEP_EDITABLE(state, { step, index }) {
      step.isEditable = isStepEditable(step, state, index)
    },
    SET_APPLICATION: (state, data) => {
      state.stepsValues = data.application_steps
      state.chosenSessions = data.selected_sessions
      state.selectedSessionsList = data.selected_sessions
      state.productPurchases = [...data.product_purchases]
      state.selectedGroupsList = data.selected_groups
      state.allSelectedGroups = data.selected_groups
      state.applicationStatus = data.status
      state.isApplicationAccepted = data.is_accepted
      state.applicationPaymentOption = data.application_payment_option
      state.paidAmountForPc = data.paid_amount_for_pc
      if (data.editing_state_version !== undefined) {
        state.editingStateVersion = data.editing_state_version
      }
      if (data.application_payment_option) {
        state.paymentContractIsExist = true
      }
    },
    PREPARE_CROSS_PROGRAMS_CONDITIONAL_FIELDS_DATA: state => {
      state.crossProgramsConditions.forEach(condition => {
        condition.scoped_conditions.forEach(subConditions => {
          subConditions.forEach(subCondition => {
            state.crossProgramConditionalFieldsData.push(...[
              {
                id: subCondition.id,
                field_id: subCondition.field_id,
                program_id: subCondition.cross_program_id,
              }
            ])
          })
        })
      })
    },
    PREPARE_CROSS_PROGRAMS_CONDITIONS: state => {
      state.userFieldResults.forEach(userFieldResult => {
          if (userFieldResult.type === 'file_upload') {
            userFieldResult.value = userFieldResult.files.map(item => item.path) || []
            userFieldResult.files = userFieldResult.files || []
          } else if (userFieldResult.type === 'avatar') {
            userFieldResult.value = userFieldResult.files?.[0]?.path || null
            userFieldResult.files = userFieldResult.files?.[0] || []
          } else if (userFieldResult.type === 'payment_field') {
            userFieldResult.payment_month_selected = userFieldResult.result?.payment_month_selected
            userFieldResult.payment_plan_activated = userFieldResult.result?.payment_plan_activated
          } else {
            userFieldResult.value = designateValue(userFieldResult.type, userFieldResult.result)
          }
      })
    },
    PREPARE_CONDITIONS_FROM_STEPS: state => {
      state.stepsList.forEach(step => {
        if (step.form) {

          state.allFormFields.push(...step.form.fields)

          let stepConditions = []
          step.form.fields.forEach(field => {
            if (field.conditions.length) {
                stepConditions.push(...field.conditions)
            }
          })

          const disabledStepsConditions = step.form.conditions.filter(condition => condition.action === 'disable_submission')
          if (disabledStepsConditions.length) {
            state.allConditions.push(...disabledStepsConditions)
          }

          stepConditions.push(...step.step_conditions)
          
          if (checkIfStepCanBeHidden(step)) {
            step.hidden = checkHiddenAttr(step.step_conditions.filter(item => item.action === 'Show'))
          }

          if (stepConditions.length) {
            state.allConditions.push(...stepConditions)
          }
        }
      })
    },
    SET_FORMS_DATA: state => {
      let index = 0
      state.stepsList.filter(step => step.form).forEach(step => {
        step.preparedFields = step.form.fields.map(field => {
          if (['avatar', 'file_upload'].includes(field.type)) step.hasFileField = true
          if (field.type === 'signature') step.hasSignatureField = true
          if (field.type === 'session_selection' && !field.options.length) {
            field.options = state.sessionsList
          }
          const ref = field.name + index
          index++

          let newField
          if (field.type === 'group_selection') {
            newField = {
              id: field.id,
              step_id: step.id,
              ref: ref,
              value: null,
              label: field.label,
              name: field.name,
              default: field.default,
              options: [],
              type: transformField(field.type),
              required: field.required,
              hide_price: field.hide_price,
              hidden: checkHiddenAttr(field.conditions.filter(item => item.action === 'Show')),
            }
          } else if (field.type === 'payment_field') {
            newField = {
              id: field.id,
              step_id: step.id,
              ref: ref,
              name: field.label,
              type: transformField(field.type),
              charge_amount: field.options.charge_amount,
              description: field.options.description,
              payment_plan_is_enable: field.options.payment_plan_is_enable,
              max_months: field.options.max_months,
              payment_plan_activated: false,
              payment_month_selected: null,
              field_id: field.id,
              hidden: checkHiddenAttr(field.conditions.filter(item => item.action === 'Show')),
            }
          } else if (field.type === 'product_field') {
            newField = {
              id: field.id,
              step_id: step.id,
              ref: ref,
              name: field.label,
              type: transformField(field.type),
              quantity: 1,
              amount: 0,
              field_id: field.id,
              product_uuid: field.default,
              hidden: checkHiddenAttr(field.conditions.filter(item => item.action === 'Show')),
            }
          } else {
            newField = {
              id: field.id,
              step_id: step.id,
              ref: ref,
              value: designateValue(field.type, field.default, field.options),
              label: field.label,
              name: field.name,
              settings: field.settings,
              options: designateOptions(field.type, field.options),
              type: transformField(field.type),
              required: field.required,
              hidden: checkHiddenAttr(field.conditions.filter(item => item.action === 'Show')),
              use_prefill: field.use_prefill,
              attribute_field_name: field.attribute_field_name,
            }
          }

          return newField
        })
      })
    },
    SET_FORMS_VALUES_FROM_USER_ATTRIBUTES: state => {
      state.userAttributes.forEach(item => {
        state.stepsList.filter(step => step.use_form_prefill).forEach(step => {
          step.preparedFields.filter(field => field.use_prefill).forEach(field => {
            if (field.name === item.key || field.attribute_field_name === item.key) {
              field.value = designateValue(field.type, item.value, field.options)
            }
          })
        })

      })
    },
    SET_FORMS_VALUES_FROM_APPLICATION_FIELDS: state => {
      state.stepsValues.forEach(stepWithValues => {
        state.stepsList.forEach(step => {
          if (stepWithValues.step_id === step.id && stepWithValues.form.id === step.form_id) {
            stepWithValues.application_fields.forEach(item => {
              step.preparedFields.forEach(field => {
                if (field.id === item.field_id) {
                  if (field.type === 'file_upload') {
                    field.value = item.files.map(item => item.path) || []
                    field.files = item.files || []
                  } else if (field.type === 'avatar') {
                    field.value = item.files?.[0]?.path || null
                    field.files = item.files?.[0] || []
                  } else if (field.type === 'payment_field') {
                    field.payment_month_selected = item.result?.payment_month_selected
                    field.payment_plan_activated = item.result?.payment_plan_activated
                  } else {
                    field.value = designateValue(field.type, item.result, field.options)
                  }
                }
              })
            })
          }
        })

      })
    },
    SET_FORMS_VALUES_FROM_FAMILIES_FIELDS: state => {
      const familyAttributes = state.familyData?.family?.attributes || {}
      Object.entries(familyAttributes).forEach(([key, value]) => {
        state.stepsList.filter(step => step.use_form_prefill).forEach(step => {
          step.preparedFields.filter(field => field.use_prefill).forEach(field => {
            if (field.name === key || field.attribute_field_name === key) {
              field.value = designateValue(field.type, value, field.options)
            }
          })
        })
      })
    },
    CHECK_CONDITIONS: state => {
      const allFields = state.stepsList
        .filter(step => step.form)
        .map(step => step.preparedFields)
        .flat()

      const updateStep = (predicate, updater) => {
        const stepIndex = state.stepsList.findIndex(predicate)
        if (stepIndex !== -1) {
          const step = state.stepsList[stepIndex]
          updater(step)
          Vue.set(state.stepsList, stepIndex, step)
        }
      }

      state.allConditions.forEach(condition => {
        let isMatched, fromStepId, fromCrossFieldResultId
        for (let i = 0; i < condition.scoped_conditions.length; i++) {
          const scopeItems = condition.scoped_conditions[i];

          const scopeItemsResults = scopeItems.filter(item => item.cross_program_id == null).map(item => {
            const foundedField = allFields.find(field => field.id === item.field_id)
            let result = null
            if (foundedField.type === "group_selection") {
              result = hasValue(item, Array.isArray(foundedField.value) ? foundedField.value.map(value => value.name) : foundedField.value)
            } else {
              result = hasValue(item, foundedField.value)
            }
            if (result) {
              fromStepId = foundedField.step_id
            }
            return result
          })

          const scopeItemsResultsCrossProgram = scopeItems.filter(item => item.cross_program_id).map(item => {
            const foundedField = state.userFieldResults.find(field => field.id === item.id)
            let result = null
            if (!foundedField) {
              return result
            }
            if (foundedField.type === "group_selection") {
              result = hasValue(item, Array.isArray(foundedField.value) ? foundedField.value.map(value => value.name) : foundedField.value)
            } else {
              result = hasValue(item, foundedField.value)
            }
            if (result) {
              fromCrossFieldResultId = foundedField.field_result_id
            }
            return result
          })

          isMatched = (scopeItemsResults.length && scopeItemsResults.every(i => i === true)) ||
            (scopeItemsResultsCrossProgram.length && scopeItemsResultsCrossProgram.every(i => i === true))

          if (isMatched || i == condition.scoped_conditions.length - 1) {
            if (condition.related_type === 'Step' && condition.action !== 'disable_submission') {
              const fromStep = state.stepsList.find(step => step.id === fromStepId)

              updateStep(
                step => step.id === condition.related_id,
                step => {
                  if (condition.action === 'Show') {
                    step.hidden = !isMatched
                    // if (step.status !== 'Approved') {
                    //   step.status = ''
                    // }
                    if (step.status === 'Approved' && step.hidden_value_in_db) {
                      step.status = ''
                    }
                  } else if (checkIfStepCanBeHidden(step, fromStep)) {
                    step.hidden = isMatched
                    step.hiddenByStep = fromStepId
                    step.hiddenByCrossFieldResult = fromCrossFieldResultId
                  }
                }
              )
            }

            if (condition.related_type === 'Field' && condition.action !== 'disable_submission') {
              const field = state.allFormFields.find(field => field.id === condition.related_id)

              if (field) {
                updateStep(
                  step => step.form?.id === field.form_id,
                  step => {
                    const updatedField = step.preparedFields.find(f => f.id === condition.related_id)
                    if (updatedField) {
                      const wasHidden = updatedField.hidden
                      updatedField.hidden = !!(condition.action === 'Show' ? !isMatched : isMatched)
                      const justBecameVisible = wasHidden && !updatedField.hidden

                      if (condition.action === 'Show' && !isMatched) {
                        updatedField.value = designateValue(updatedField.type, null, field.options)
                      }

                      // Restore pre-fill / saved value only when field transitions hidden → visible.
                      // Running on every re-evaluation would overwrite user-entered data.
                      // File fields are excluded from pre-fill restoration.
                      if (justBecameVisible && !['file_upload', 'avatar'].includes(updatedField.type)) {
                        // 1. Try to restore from previously saved application fields
                        let restored = false
                        const stepValues = state.stepsValues.find(
                          sv => sv.step_id === step.id && sv.form?.id === step.form_id
                        )
                        if (stepValues) {
                          const appField = stepValues.application_fields.find(f => f.field_id === updatedField.id)
                          if (appField) {
                            updatedField.value = designateValue(updatedField.type, appField.result, updatedField.options)
                            restored = true
                          }
                        }

                        // 2. Fall back to user-attribute / family-data pre-fill
                        if (!restored && updatedField.use_prefill && step.use_form_prefill) {
                          const attrItem = state.userAttributes.find(
                            item => item.key === updatedField.name || item.key === updatedField.attribute_field_name
                          )
                          if (attrItem) {
                            updatedField.value = designateValue(updatedField.type, attrItem.value, updatedField.options)
                            restored = true
                          }

                          if (!restored) {
                            const familyAttributes = state.familyData?.family?.attributes || {}
                            const key = updatedField.attribute_field_name || updatedField.name
                            if (key && key in familyAttributes) {
                              updatedField.value = designateValue(updatedField.type, familyAttributes[key], updatedField.options)
                            }
                          }
                        }
                      }

                      // check if it is possible to hide the whole step
                      if (step.preparedFields.every(item => item.hidden === true) && checkIfStepCanBeHidden(step)) {
                        step.hidden = true
                        step.hiddenByStep = fromStepId
                      }
                    }
                  }
                )
              }
            }

            if (condition.action === 'disable_submission') {
              const forceDisablingSendButton = !!isMatched
              const infoMessage = condition.settings.find(s => s.key === 'info_message')
              const disabledButtonInfoMessage = (isMatched && infoMessage.value) ?  infoMessage.value : ''

              scopeItems
                .filter(item => item.cross_program_id == null)
                .forEach(item => {
                  const field = state.allFormFields.find(field => field.id === item.field_id)

                  if (field) {
                    updateStep(
                      step => step.form?.id === field.form_id,
                      step => {
                        const updatedField = step.preparedFields.find(f => f.id === item.field_id)
                        if (updatedField) updatedField.customInvalid = forceDisablingSendButton
                      })
                  }
              })

              if (condition.related_type === 'Form') {
                updateStep(
                  step => step.form?.id === condition.related_id,
                  step => {
                    step.forceDisablingSendButton = forceDisablingSendButton
                    step.disabledButtonInfoMessage = disabledButtonInfoMessage
                  })
              }

              if (condition.related_type === 'Step') {
                updateStep(
                  step => step.id === condition.related_id,
                  step => {
                    step.forceDisablingSendButton = forceDisablingSendButton
                    step.disabledButtonInfoMessage = disabledButtonInfoMessage
                  })
              }
            }
            break
          }
        }
      })
    },
    UPDATE_RELATED_VALUE: (state, { fromStepId, updatedFieldId, value }) => {
      const stepIndex = state.stepsList.findIndex(step => step.id === fromStepId)
      if (stepIndex !== -1) {
        const step = state.stepsList[stepIndex]

        const updatedField = step.preparedFields.find(f => f.id === updatedFieldId)

        if (updatedField) {
          updatedField.value = value

          Vue.set(state.stepsList, stepIndex, step)
        }
      }
    },
    UPDATE_RELATED_VALUE_IN_FAMILY_FORM: (state, { updatedFieldId, value }) => {
      const updatedField = state.familyFormFields.find(f => f.field_id === updatedFieldId)
      if (updatedField) {
        updatedField.value = value
      }
    },
    CHECK_IF_ALL_FIELDS_IN_STEP_ARE_HIDDEN: state => {
      state.stepsList.forEach(step => {
        if (step.preparedFields && step.preparedFields.every(item => item.hidden === true)) {
          step.hidden = true
        }
      })
    },
    SET_APPLICATION_STATUS: (state, data) => {
      state.applicationStatus = data
    },
    SET_IS_APPLICATION_ACCEPTED: (state, data) => {
      state.isApplicationAccepted = data
    },
    SET_PAYMENT: (state, data) => {
      state.paymentContract = JSON.parse(JSON.stringify(data))

      if (state.applicationPaymentOption) {
        state.paymentContract.options = data.options.filter(item => item.id == state.applicationPaymentOption.condition_id)
        state.paymentContract.selectedOption = Number(state.applicationPaymentOption.condition_id)
        if (state.paymentContract.options.length) {
          state.paymentContract.options[0].amountOfPayments = state.applicationPaymentOption.count_of_payments
          state.paymentContract.options[0].startDate = moment(state.applicationPaymentOption.start_date, 'YYYY-MM-DD').format('MM/DD/YYYY')
          state.paymentContract.options[0].selectedMethod = state.applicationPaymentOption.payment_method
          if (state.applicationPaymentOption.custom_schedule) {
            state.paymentContract.options[0].customSchedule = state.applicationPaymentOption.custom_schedule
          }
        }
      } else {
        state.paymentContract.options = data.options.filter(item => item.status === 1 && (item.user_id === null || item.user_id === state.userId))

        const today = moment()

        state.paymentContract.options = state.paymentContract.options
          // check option availability by activeFrom / activeTo
          .filter(option => {
            const activeFrom = option.settings?.activeFrom ? moment(option.settings.activeFrom) : null
            const activeTo = option.settings?.activeTo ? moment(option.settings.activeTo) : null

            if ((activeFrom && today.isBefore(activeFrom)) || (activeTo && today.isAfter(activeTo))) {
              return false
            }
            return true
          })
          // 2) filter option methods by period availability if exist
          .map(option => {
            if (option.settings?.enablePaymentMethodPeriod && Array.isArray(option.settings.paymentPeriods)) {
              option.method = option.method.filter(method => {
                const periods = option.settings?.paymentPeriods || []

                if (!option.settings?.enablePaymentMethodPeriod || periods.length === 0) {
                  return true
                }

                const methodPeriods = periods.filter(period => period.method === method)

                if (methodPeriods.length === 0) {
                  return true
                }

                const allowed = methodPeriods.some(period => {
                  const from = period.from ? moment(period.from) : null
                  const to = period.to ? moment(period.to) : null

                  if ((from && today.isBefore(from)) || (to && today.isAfter(to))) {
                    return false
                  }

                  return true
                })
                return allowed
              })
            }
            return option
          })

        state.paymentContract.selectedOption = state.paymentContract.options[0].id

        state.paymentContract.options.forEach(option => {
          if (option.defaults) {
            option.amountOfPayments = option.defaults.paymentAmount
            option.selectedMethod = option.defaults?.selectedMethod
            if (option.defaults.startDate) {
              option.startDate = moment(option.defaults.startDate, 'YYYY-MM-DD').format('MM/DD/YYYY')
            }
          } else {
            if (option.schedule_option.timeSchedule == 'Split over') {
              option.amountOfPayments = option.schedule_option.paymentAmountRange.from
              option.startDate = null
            } else {
              option.amountOfPayments = 1
            }

            if (option.method.includes('Credit Card')) {
              option.selectedMethod = 'Credit Card'
            } else {
              option.selectedMethod = option.method[0]
            }
          }

          option.schedule_option.endDate = moment(option.schedule_option.endDate).format('MM/DD/YYYY')
        })
      }
    },
    SET_STEP_STATUS: (state) => {
      state.stepsValues.forEach(item => {
        state.stepsList.forEach(step => {
          if (step.id === item.step_id) {
            step.status = item.step_status
            step.info_message = item.info_message
            step.hidden = item.is_hidden
            step.hidden_value_in_db = item.is_hidden
            step.is_last_submitted_step = item.is_last_submitted_step
          }
        })
      })
    },
    SET_CHOSEN_SESSIONS: (state, data) => {
      state.chosenSessions = data
    },
    UPDATE_ALL_SELECTED_GROUPS: (state, addedGroups = []) => {
      state.allSelectedGroups = []
      state.selectedGroupsList.forEach(group => {
        state.allSelectedGroups.push(group)
      })
      addedGroups.forEach(newGroup => {
        const group = state.allSelectedGroups.find(group => group.id === newGroup.id)
        if (!group) {
          state.allSelectedGroups.push({
            id: newGroup.id,
            name: newGroup.name,
            price: newGroup.price,
            type: newGroup.type,
            has_tuition_fee: newGroup.has_tuition_fee,
          })
        }
      })
    },
    SET_GROUPS_FOR_AUTO_ASSIGN: (state, data) => {
      data.reduce((options, group) => {
        group.price = group.sessions.reduce((totalPrice, session) => {
          totalPrice += session.pivot_price
          return totalPrice
        }, 0)
        options.push(group)
        return options
      }, []).forEach(newGroup => {
        state.groupsForAutoAssign.push({
          id: newGroup.id,
          name: newGroup.name,
          price: newGroup.price,
          type: newGroup.type,
          has_tuition_fee: newGroup.has_tuition_fee,
        })
      })
    },
    RESET_STATE(state) {
      Object.assign(state, defaultState())
    },
    SET_TAB_INDEX: (state, data) => {
      if (data >= 0) {
        state.currentTabIndex = data
      }
    },
    INCREMENT_TAB_INDEX: (state) => {
      state.currentTabIndex++
    },
    DECREMENT_TAB_INDEX: (state) => {
      state.currentTabIndex--
    },
    SET_SELECTED_GROUP_FOR_VIEWING_USERS: (state, groupId) => {
      state.selectedGroupForViewingUsers = groupId
    },
    SET_APPLICATION_PAYMENT_OPTION: (state, data) => {
      state.applicationPaymentOption = data
      if (state.applicationPaymentOption) {
        state.paymentContractIsExist = true
        state.paymentContract.options = state.paymentContract.options.filter(item => item.id == state.applicationPaymentOption.condition_id)
        state.paymentContract.selectedOption = Number(state.applicationPaymentOption.condition_id)
        if (state.paymentContract.options.length) {
          state.paymentContract.options[0].amountOfPayments = state.applicationPaymentOption.count_of_payments
          state.paymentContract.options[0].startDate = moment(state.applicationPaymentOption.start_date, 'YYYY-MM-DD').format('MM/DD/YYYY')
          state.paymentContract.options[0].selectedMethod = state.applicationPaymentOption.payment_method
          if (state.applicationPaymentOption.custom_schedule) {
            state.paymentContract.options[0].customSchedule = state.applicationPaymentOption.custom_schedule
          }
        }
      }
    },
    DISABLE_EDITING_STEPS_IF_PAYMENT_CONTRACT_IS_SELECTED: state => {
      if (state.paymentContractIsExist) {
        for (let i = 0; i < state.stepsList.length; i++) {
          state.stepsList[i].isDisabledEditingByPc = true
          if (state.stepsList[i].hasPC) break
        }
      }
    },
    SET_IS_DATA_FULL_PRAPARED: (state, val) => {
      state.isDataFullPrepared = val
    },
    SET_GROUP_SELECTION_FORM_STATE_FIELD(state, { fieldName, value }) {
      state.groupSelectionFormState[fieldName] = value
    },
    SET_GROUP_OPTIONS(state, allGroups ) {
      state.groupSelectionFields.forEach(groupSelectionField => {
        const allGroupsByType = allGroups.filter(group => group.type === groupSelectionField.default)

        const stepIndex = state.stepsList.findIndex(step => step.form_id === groupSelectionField.form_id)
        if (stepIndex !== -1) {
          const step = state.stepsList[stepIndex]

          const updatedField = step.preparedFields.find(f => f.id === groupSelectionField.id)

          if (updatedField) {
            updatedField.options = allGroupsByType
            Vue.set(state.stepsList, stepIndex, step)
          }
        }
      })
    },
  },
  actions: {
    async updateGroupsList({ commit, state, dispatch }) {
      const queryParams = {
        program_id: state.programId,
        application_id: state.applicationId,
        context: { ...state.groupSelectionFormState },
        session_id: state.chosenSessions.map(session => session.id),
        group_type: state.groupSelectionFields.map(field => field.default)
      }

      try {
        const res = await dispatch('fetchApplicationAvailableGroupOptions', queryParams)

        commit('SET_GROUP_OPTIONS', res)
      } catch (err) {
        console.error('Error updating groups list:', err)
      }
    },
    recalculateStepEditable({ commit, state }, stepId) {
      const index = state.stepsList.findIndex(s => s.id === stepId)
      if (index !== -1) {
        const step = state.stepsList[index]
        commit('UPDATE_STEP_EDITABLE', { step, index })
      }
    },
    async prepareFamilyForm({ state, commit }, params) {
      try {
        commit('SET_FAMILY_SETTINGS', params.family_settings)
        commit('SET_FAMILY_FORM_STATUS_INFO')
        commit('SET_FAMILY_FORM', params.family_form)
        if (state.familyData.secondary_parent) {
          commit('ADD_SECONDARY_PARENT')
        }
        commit('SET_FAMILY_FORM_VALUES')
      } catch (error) {
        return error
      }
    },
    fetchChildData(ctx, id) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/my-children/${id}`)
          .then(response => {
            ctx.commit('SET_USER_DATA', response.data.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchUserAttributes(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/user-attributes', {
            params: queryParams,
          })
          .then(response => {
            ctx.commit('SET_USER_ATTRIBUTES', response.data.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchFamilyData(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/family-data', {
            params: queryParams,
          })
          .then(response => {
            ctx.commit('SET_FAMILY_DATA', response.data.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchApplicationGroupOptions(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/applications/groups`, queryParams)
          .then(response => {
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    fetchApplicationAvailableGroupOptions(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/applications/available-groups`, queryParams)
          .then(response => {
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    saveProgress(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/applications/save-progress', queryParams)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            // Server responded with an error status (e.g. 422/409) - resolve with
            // the response so the caller can keep inspecting status/data like before.
            if (error.response) {
              resolve(error.response)
              return
            }
            // Network/timeout failure - there is no response to resolve with.
            // Reject so the caller's catch() runs instead of silently resolving
            // with undefined and blowing up later on response.status/response.data.
            reject(error)
          })
      })
    },
    saveFamilyForm(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/family-form/save-data', queryParams)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            // Server responded with an error status (e.g. 422 validation) - resolve
            // with the response so the caller can keep inspecting status/data like before.
            if (error.response) {
              resolve(error.response)
              return
            }
            // Network/timeout failure - there is no response to resolve with.
            // Reject so the caller's catch() runs instead of silently resolving
            // with undefined and blowing up later on response.status/response.data.
            reject(error)
          })
      })
    },
    fetchGroupUsersList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/applications/group-members', {
            params: queryParams,
          })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchAutoAssignedGroupsList(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .get('/applications/auto-assigned-groups', {
            params: queryParams,
          })
          .then(response => {
            ctx.commit('SET_GROUPS_FOR_AUTO_ASSIGN', response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    fetchUserFieldResults(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/user-field-results', queryParams)
          .then(response => {
            ctx.commit('SET_USER_FIELD_RESULTS', response.data.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchPaymentFees(ctx, programId) {
      return new Promise((resolve, reject) => {
        axios
          .get('/payment-fees', { params: { program_id: programId } })
          .then(response => {
            ctx.commit('SET_PAYMENT_FEES', response.data.data || [])
            resolve(response.data.data || [])
          })
          .catch(error => {
            ctx.commit('SET_PAYMENT_FEES', [])
            reject(error)
          })
      })
    },
    fetchScheduledPayments(ctx, queryParams) {
      return new Promise((resolve, reject) => {
        axios
          .post('/applications/selected-pc-option-details', queryParams )
          .then(response => {
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
  },
}